import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { Link, useNavigate } from 'react-router-dom';
import { Mongo } from 'meteor/mongo';
import { ModelListPage, ModelListPageStyled } from '/imports/ui/ModelListPage';
import { generateSolarBillPdf, modelClass, SolarBill } from '/imports/api/solarBills';
import { filterBoolean, formatCurrency, formatDate, formatDateTime, formatNumber, queryStringToObject } from '/imports/utils';
import { strings } from '/imports/strings';
import { fileHref } from '/imports/api/files';
import { Icon } from '/imports/ui/Icon';
import { PageHeading } from '/imports/ui/PageHeading';
import { Button } from '/imports/ui/Button';
import { useFindOne, useMethod, useUser } from '/imports/hooks';
import { Org, Orgs } from '/imports/api/orgs';
import { NorthShoreSendBillsModal } from '/imports/ui/NorthshoreSendBillsModal';
import { RealtyIncomeSendBillsModal } from '/imports/ui/RealtyIncomeSendBillsModal';
import { CalpineSendBillsModal } from '/imports/ui/CalpineSendBillsModal';
import { createNorthshoreConsolidatedBill } from '/imports/api/northshoreBilling';
import { modelClass as productionMeterClass } from '/imports/api/productionMeters';
import { toastOnError } from '/imports/ui/utils';
import { Loading } from '/imports/ui/Loading';
import { GmapsAddress } from '/imports/gmaps';
import { RichSelect } from '/imports/ui/RichSelect';
import { stateOptions } from '/imports/options';
import { DateInput } from '/imports/ui/DateInput';
import { toast } from 'react-toastify';

/** Formats a date string (anchored at noon UTC to avoid TZ shifts). */
const formattedDate = (date: string) => formatDate(new Date(`${date}T12:00:00.000Z`));

const SolarBillsPageStyled = styled(ModelListPageStyled)`
  table {
    font-size: 0.85rem;
  }
  td {
    &:nth-child(3) {
      max-width: 11rem;
    }
  }
  .header-buttons {
    display: flex;
    gap: 0.5rem;
  }
  .extra-filters {
    display: flex;
    gap: 1rem;
  }
`;

/** List page for solar bills with org-specific send/consolidate/PDF actions and filters. */
export const SolarBillsPage = () => {
  const navigate = useNavigate();
  const queryParams = queryStringToObject();
  const batch = queryParams.batch;
  const user = useUser();
  const orgId = user?.orgId;
  const org = useFindOne(() => Orgs.find(orgId), [orgId]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [showSendModal, setShowSendModal] = useState(false);
  const [productionMeters,] = useMethod(productionMeterClass.listMethod, { limit: 10000 });
  const _serviceAddresses = useMemo(() => productionMeters?.documents?.map((m) => m.address), [productionMeters]);
  const serviceAddresses = filterBoolean((_serviceAddresses || []).filter((a) => a?.formatted));
  const _accountNumbers = useMemo(() => productionMeters?.documents?.map((m) => m.billingAccountNumber), [productionMeters]);
  const accountNumbers = filterBoolean(_accountNumbers || []);
  const [query, setQuery] = useState<Mongo.Query<SolarBill>>(batch ? { batch: parseInt(batch) } : {});
  /**
   * Picks the send-bills modal for the org's billing integration.
   * @param org - the current org
   * @param selectedIds - selected bill ids to send
   * @returns the matching modal element, or null
   */
  const getSendModalComponent = (org: Org, selectedIds: string[]) => {
    const sid = org.sid;
    if (sid === 'northshore') return <NorthShoreSendBillsModal billIds={selectedIds} onClose={() => setShowSendModal(false)} />;
    if (sid === 'dollarGeneral' || sid === 'tractorSupply' || sid === 'ultaBeauty') return <RealtyIncomeSendBillsModal billIds={selectedIds} onClose={() => setShowSendModal(false)} />;
    if (sid === 'calpine') return <CalpineSendBillsModal billIds={selectedIds} onClose={() => setShowSendModal(false)} />;
    return null;
  };
  const [creatingNorthshoreConsolidatedBill, setCreatingNorthshoreConsolidatedBill] = useState(false);
  /** Creates a consolidated Northshore bill from the selected bills and navigates to it. */
  const _createNorthshoreConsolidatedBill = toastOnError(async () => {
    if (!selectedIds.length) return;
    setCreatingNorthshoreConsolidatedBill(true);
    const consolidatedBill = await createNorthshoreConsolidatedBill.call({ billIds: selectedIds });
    setCreatingNorthshoreConsolidatedBill(false);
    navigate(`/solar-bills/${consolidatedBill._id}`);
  });
  /** Generates PDFs for each selected bill, showing cancellable progress in a toast. */
  const generateSelectedBillPdfs = toastOnError(async () => {
    let i = 0;
    const toastId = toast.success(`${strings.Generated()} ${i} / ${selectedIds.length}`, { autoClose: Number.MAX_SAFE_INTEGER, onClose: () => { cancelled = true; } });
    let cancelled = false;
    for (const _id of selectedIds || []) {
      if (cancelled) break;
      await generateSolarBillPdf.call(_id);
      i += 1;
      toast.update(toastId, { render: `${strings.Generated()} ${i} / ${selectedIds.length}`});
    }
    toastId && toast.dismiss(toastId);
  });
  return <ModelListPage
    permission={() => !!user?.isSuperUser}
    modelClass={modelClass}
    styled={SolarBillsPageStyled}
    onSelectedIds={(selectedIds) => setSelectedIds(selectedIds)}
    header={(
      <PageHeading
        left={<h1>{strings.SolarBills()}{batch ? <span> ({strings.Batch()}: {batch})</span> : null}</h1>}
        right={
          <div className="header-buttons">
            {selectedIds.length ? <Button onClick={() => setShowSendModal(true)}>{strings.SendBills()} ({selectedIds.length})</Button> : null}
            {selectedIds.length ? <Button onClick={() => generateSelectedBillPdfs()}>{strings.GeneratePdf()} ({selectedIds.length})</Button> : null}
            {
              selectedIds.length && org.sid === 'northshore' ?
                creatingNorthshoreConsolidatedBill ? <Button><Loading /></Button> : <Button onClick={() => _createNorthshoreConsolidatedBill()}>{strings.ConsolidateBill()} ({selectedIds.length})</Button>
                : null
            }
            <Link to="/solar-bills/new"><Button>{strings.GenerateBills()}</Button></Link>
          </div>
        }
      />
    )}
    columns={[
      { field: 'pdf', label: 'PDF', render: (doc) => doc.pdf?.url ? <a href={fileHref(doc.pdf)} target="_blank" rel="noreferrer"><Icon icon="docs" /></a> : '' },
      { field: 'billingPeriod', render: (doc) => <Link to={`${modelClass.pathPrefix}/${doc._id}`}>{formattedDate(doc.startDate).substring(0, 5)}→{formattedDate(doc.endDate)}</Link> },
      { field: 'accountNumber' },
      { field: 'address', render: (doc) => doc.serviceAddress?.formatted, width: '15rem' },
      { field: 'meterIds', label: strings.Meter(), render: (doc) => <Link to={`/production-meters/${doc.meterIds.at(0)}`} target="_blank"><Icon icon="openInNew" /></Link>, width: '4rem' },
      { field: 'production', render: (doc) => <span>{formatNumber(doc.production)} kWh</span> },
      { field: 'solarCharges', render: (doc) => formatCurrency((doc.ppaCharges || 0) + (doc.fixedCharges || 0)), width: '3rem' },
      { field: 'previousCharges', render: (doc) => formatCurrency((doc.previousCharges || 0)), width: '3rem' },
      { field: 'lateCharges', render: (doc) => formatCurrency((doc.lateCharges || 0)), width: '3rem' },
      { field: 'credit', render: (doc) => formatCurrency((doc.credits || 0)), width: '3rem' },
      { field: 'dueDate', render: (doc) => <span>{formattedDate(doc.dueDate)}</span> },
      { field: 'createdAt', render: (doc) => <span>{doc.createdAt && formatDateTime(doc.createdAt)}</span> },
      { field: 'batch' },
    ]}
    extraFilters={(
      <div className="extra-filters">
        <RichSelect
          value={query.accountNumber as string}
          options={[...(accountNumbers || []).map((a) => ({ value: a, label: a }))]}
          onChange={(option) => setQuery({ ...query, accountNumber: option.value })}
          onClear={() => setQuery({ ...query, accountNumber: undefined })}
          placeholder="Filter by Account"
        />
        <RichSelect
          value={(query.serviceAddress as GmapsAddress)?.formatted}
          options={[...(serviceAddresses || []).map((a) => ({ value: a.formatted, label: a.formatted }))]}
          onChange={(option) => setQuery({ ...query, ['serviceAddress.formatted']: option.value })}
          onClear={() => setQuery({ ...query, 'serviceAddress.formatted': undefined })}
          placeholder="Filter by Address"
        />
        <RichSelect
          value={query.state}
          options={[...stateOptions]}
          onChange={(option) => setQuery({ ...query, 'serviceAddress.state': option.value })}
          onClear={() => setQuery({ ...query, 'serviceAddress.state': undefined })}
          placeholder="Filter by State"
        />
        <DateInput
          value={query.issuedDate as string}
          onChange={((issuedDate) => setQuery({ ...query, issuedDate }))}
          onClear={() => setQuery({ ...query, issuedDate: undefined })}
        />
      </div>
    )}
    defaultSort={{ createdAt: -1 }}
    query={query}
    footer={
      showSendModal ?
        getSendModalComponent(org, selectedIds)
        :
        null
    }
  />;
};